/* React */
import type { RefObject } from "react";

/* d3.js */
import * as d3 from "d3";

/* Helper */
import {
  appendNoValueBins,
  computeHistogramCountsPerBin,
  createGlobalBins,
  createGradients,
  createXScale,
  createYScale,
  drawAxes,
  drawBarsWithRoundedCorners,
  drawNoValueBinLabels,
} from "src/components/charts/stacked-histogram/helper";

/* Utils */
import {
  computePlotHeightWidth,
  createViewBox,
} from "src/utils/helper/charts/helper";

/* Types */
import { SVGSelection } from "src/shared/types/d3";

/* Interfaces */
import { ChartTooltipRef } from "src/shared/interfaces/ChartTooltip";
import { ChartDimensionsProps } from "src/shared/interfaces/charts/BaseChart";
import { HistogramProps } from "src/shared/interfaces/charts/Histogram";

export const createStackedHistogram = (
  svg: SVGSelection,
  tooltipRef: RefObject<ChartTooltipRef>,
  data: HistogramProps[],
  dimensions: ChartDimensionsProps,
  unit?: string,
  showNoValueEntries?: boolean
) => {
  if (!data) return;
  createViewBox(svg, dimensions);

  const { margin, height, width } = computePlotHeightWidth(
    dimensions,
    { top: 62, right: 92, bottom: 32, left: 92 }
  );

  const allXValues = data.flatMap((d) => d.x ?? []);

  const xScale = createXScale(allXValues, width);

  const {
    binGenerator,
    bins: globalBins,
    categories: binCategories,
  } = createGlobalBins(
    allXValues,
    xScale.domain() as [number, number],
    10
  );

  const subgroups = data.map((d) => d.name ?? "unnamed");

  const binFormattedDataForStack =
    computeHistogramCountsPerBin(
      data,
      binGenerator,
      globalBins.length
    );

  const { formattedDataForStack, categories } =
    showNoValueEntries
      ? appendNoValueBins(
          binFormattedDataForStack,
          binCategories,
          data
        )
      : {
          formattedDataForStack: binFormattedDataForStack,
          categories: binCategories,
        };

  const stack = d3
    .stack<Record<string, number>>()
    .keys(subgroups)(formattedDataForStack);

  const yScale = createYScale(
    formattedDataForStack,
    subgroups,
    height
  );

  const g = svg
    .append("g")
    .attr(
      "transform",
      `translate(${margin.left},${margin.top})`
    );

  const xBand = (d3 as any)
    .scaleBand()
    .domain(categories)
    .range([0, width])
    .padding(0.1);

  // Draw axes. The x-axis is derived from `xBand` + the numeric bins so ticks
  // always line up with the bars (see drawAxes), hence it is built after xBand.
  drawAxes(
    g,
    xScale,
    xBand,
    globalBins,
    binCategories,
    yScale,
    height,
    width,
    unit
  );

  if (showNoValueEntries) {
    drawNoValueBinLabels(
      g,
      xBand,
      height,
      categories.slice(binCategories.length)
    );
  }

  createGradients(svg, subgroups);
  drawBarsWithRoundedCorners(
    svg,
    g,
    stack,
    subgroups,
    categories,
    xBand,
    yScale,
    tooltipRef
  );
};
